import { useContext } from "react";

import CommentableModule from "containers/admin/clients/touchpoint/components/comments/commentable-module/commentable-module";
import { DesignPreviewSandbox } from "containers/admin/clients/touchpoint/components/design-preview-sandbox/design-preview-sandbox";

import CommentContext from "contexts/comment-context";

import { usePreviewSrcDoc } from "./hooks/use-preview-srcdoc";

import {
  INITIAL_IFRAME_HEIGHT,
  MIN_PREVIEW_HEIGHT,
  MOBILE_VIEWPORT_WIDTH,
  PREVIEW_SANDBOX,
} from "./config";

import { IframeTouchpointPreviewProps } from "./types";

/**
 * Renders touchpoint `finalHtml` in a sandboxed iframe (opaque origin, see `PREVIEW_SANDBOX`) and overlays
 * comment pins aligned to module rects reported over postMessage. Comment mode renders statically (author
 * scripts off) so every block is commentable; `isFullContentMode` renders statically too but keeps links
 * live and shows no pins; otherwise the author JS runs. Toggling either rebuilds `srcDoc`.
 */
const IframeTouchpointPreview = ({
  htmlContent,
  htmlJSON,
  isCommentMode = false,
  isFullContentMode = false,
  mobileView = false,
  emailHeaders,
  hideSubjectAndPreheader,
  touchpointName,
  baseHref,
}: IframeTouchpointPreviewProps) => {
  const { commentData } = useContext(CommentContext);
  const { srcDoc, documentKey, iframeRef, modules, contentHeight, labels } = usePreviewSrcDoc({
    htmlContent,
    htmlJSON,
    isCommentMode,
    isFullContentMode,
    mobileView,
    touchpointName,
    baseHref,
  });

  return (
    <section aria-label={labels.regionLabel}>
      <DesignPreviewSandbox
        data-testid="iframe-preview-sandbox"
        isCommentMode={isCommentMode}
        isMobileView={mobileView}
        emailHeaders={emailHeaders}
        hideSubjectAndPreheader={hideSubjectAndPreheader}>
        <div
          className="relative mx-auto"
          style={{ width: mobileView ? MOBILE_VIEWPORT_WIDTH : "100%" }}>
          <iframe
            // Remounts on every rebuild so the new document replaces a fresh frame's `about:blank`
            // instead of pushing onto the shared session history — see `usePreviewSrcDoc`.
            key={documentKey}
            ref={iframeRef}
            title={labels.iframeTitle}
            srcDoc={srcDoc}
            sandbox={PREVIEW_SANDBOX}
            referrerPolicy="no-referrer"
            tabIndex={-1}
            className="block w-full border-0"
            style={{
              height: contentHeight || INITIAL_IFRAME_HEIGHT,
              minHeight: MIN_PREVIEW_HEIGHT,
            }}
            data-testid="touchpoint-preview-iframe"
          />
          {isCommentMode && (
            <div className="absolute inset-0" style={{ pointerEvents: "none" }}>
              {modules.map((module) => (
                <div
                  key={module.id}
                  style={{
                    position: "absolute",
                    top: module.top,
                    left: module.left,
                    width: module.width,
                    height: module.height,
                    pointerEvents: "auto",
                  }}>
                  <CommentableModule
                    moduleId={module.id}
                    comment={commentData.items.find(
                      (comment) => comment.annotationId === module.id,
                    )}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </DesignPreviewSandbox>
      <p aria-live="polite" role="status" className="sr-only" data-testid="iframe-preview-status">
        {labels.statusMessage}
      </p>
    </section>
  );
};

export default IframeTouchpointPreview;
